// Two-step sign-in screens and the bot check widget, shared by sign-in, settings and the "2FA required" gate
import { LogoMark } from './brand';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { api } from './api';
import { LanguageSwitch, toast, toastError } from './ui';

/** 6-digit input that also accepts a recovery code (xxxxx-xxxxx) */
export function CodeInput({ value, onChange, autoFocus = true }: { value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  const { t } = useTranslation();
  return <input className="code-input" value={value} autoFocus={autoFocus} autoComplete="one-time-code" inputMode="text" maxLength={20}
    placeholder="123456" aria-label={t('mfa.code')} onChange={e => onChange(e.target.value)} />;
}

/** Turn on two-step sign-in: QR → code → recovery codes */
export function MfaSetup({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const s = await api.post<{ secret: string; otpauth_url: string }>('/auth/2fa/setup');
      setSetup({ ...s, qr: await QRCode.toDataURL(s.otpauth_url, { margin: 1, width: 220 }) });
    } catch (e) { toastError(e); } finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ recovery_codes: string[] }>('/auth/2fa/enable', { code: code.trim() });
      // Keep the codes on screen: the session/status queries are refreshed only after "I saved them"
      setCodes(r.recovery_codes);
    } catch (e) { toastError(e); } finally { setBusy(false); }
  };
  if (codes) return <RecoveryCodes codes={codes} onDone={async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ['me'] }), qc.invalidateQueries({ queryKey: ['mfa'] })]);
    onDone?.();
  }} />;
  if (!setup) return <div className="stack">
    <p className="sub" style={{ margin: 0 }}>{t('mfa.why')}</p>
    <div><button className="btn primary" disabled={busy} onClick={() => void start()}>{t('mfa.turn_on')}</button></div>
  </div>;
  return (
    <div className="mfa-setup">
      <ol className="steps-list">
        <li>{t('mfa.step_app')}</li>
        <li>{t('mfa.step_scan')}</li>
      </ol>
      <div className="mfa-qr"><img src={setup.qr} alt={t('mfa.qr')} width={220} height={220} /></div>
      <details><summary className="sub">{t('mfa.cant_scan')}</summary><code className="mono secret">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code></details>
      <ol className="steps-list" start={3}><li>{t('mfa.step_code')}</li></ol>
      <div className="toolbar"><CodeInput value={code} onChange={setCode} />
        <button className="btn primary" disabled={busy || code.trim().length < 6} onClick={() => void enable()}>{t('mfa.confirm')}</button></div>
    </div>
  );
}

export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone?: () => void | Promise<void> }) {
  const { t } = useTranslation();
  const text = `VegaPart recovery codes\n${codes.join('\n')}\n`;
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    a.download = 'vegapart-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="stack">
      <div className="card specs">{t('mfa.save_codes')}</div>
      <div className="recovery-codes mono">{codes.map(c => <span key={c}>{c}</span>)}</div>
      <div className="toolbar">
        <button className="btn" onClick={() => { void navigator.clipboard?.writeText(text).then(() => toast(t('mfa.copied'))); }}>{t('mfa.copy')}</button>
        <button className="btn" onClick={download}>{t('mfa.download')}</button>
        {onDone && <button className="btn primary" onClick={() => void onDone()}>{t('mfa.saved_them')}</button>}
      </div>
    </div>
  );
}

/** The "enter your code" step, used after the password and when a session must be verified */
export function CodeStep({ onSubmit, title }: { onSubmit: (code: string) => Promise<void>; title?: string }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form className="stack" onSubmit={async e => {
      e.preventDefault();
      setBusy(true);
      try { await onSubmit(code.trim()); } catch (err) { toastError(err); } finally { setBusy(false); }
    }}>
      {title && <b>{title}</b>}
      <p className="sub" style={{ margin: 0 }}>{t('mfa.enter_code')}</p>
      <CodeInput value={code} onChange={setCode} />
      <button className="btn primary" type="submit" disabled={busy || code.trim().length < 6}>{t('mfa.verify')}</button>
      <p className="sub" style={{ margin: 0 }}>{t('mfa.lost_phone')}</p>
    </form>
  );
}

/** Settings card: status, turn on/off, new recovery codes */
export function MfaCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['mfa'], queryFn: () => api.get<{ enabled: boolean; recovery_codes_left: number }>('/auth/2fa'), refetchOnWindowFocus: false });
  const [mode, setMode] = useState<'off' | 'codes' | null>(null);
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  if (!q.data) return null;
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['mfa'] }), qc.invalidateQueries({ queryKey: ['me'] })]);
  if (!q.data.enabled) return <MfaSetup />;
  if (codes) return <RecoveryCodes codes={codes} onDone={() => { setCodes(null); setMode(null); void refresh(); }} />;
  return (
    <div className="stack">
      <div><span className="badge b-ok">✓ {t('mfa.on')}</span> <span className="sub">{t('mfa.codes_left', { n: q.data.recovery_codes_left })}</span></div>
      {mode === null && <div className="toolbar">
        <button className="btn" onClick={() => setMode('codes')}>{t('mfa.new_codes')}</button>
        <button className="btn" onClick={() => setMode('off')}>{t('mfa.turn_off')}</button>
      </div>}
      {mode && <div className="stack" style={{ maxWidth: 360 }}>
        {mode === 'off' && <label className="f">{t('auth.password')}<input type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="current-password" /></label>}
        <label className="f">{t('mfa.code')}<CodeInput value={code} onChange={setCode} autoFocus={mode === 'codes'} /></label>
        <div className="toolbar">
          <button className="btn primary" onClick={async () => {
            try {
              if (mode === 'off') { await api.post('/auth/2fa/disable', { password: pw, code: code.trim() }); toast(t('mfa.turned_off')); setMode(null); await refresh(); }
              else setCodes((await api.post<{ recovery_codes: string[] }>('/auth/2fa/recovery-codes', { code: code.trim() })).recovery_codes);
              setPw(''); setCode('');
            } catch (e) { toastError(e); }
          }}>{mode === 'off' ? t('mfa.turn_off') : t('mfa.new_codes')}</button>
          <button className="btn" onClick={() => setMode(null)}>{t('common.cancel')}</button>
        </div>
      </div>}
    </div>
  );
}

/** Full-screen gate when 2FA is required (company policy or VegaPart staff) but this session is not verified */
export function MfaGate({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return (
    <div className="auth"><div className="card stack" style={{ maxWidth: 460 }}>
      <div className="auth-top"><div className="brand"><LogoMark />{t('app.name')}</div><LanguageSwitch signedIn /></div>
      <h2 style={{ margin: 0 }}>{t('mfa.required_title')}</h2>
      <p className="sub" style={{ margin: 0 }}>{t('mfa.required_hint')}</p>
      {enabled
        ? <CodeStep onSubmit={async code => { await api.post('/auth/2fa/verify', { code }); await qc.invalidateQueries({ queryKey: ['me'] }); }} />
        : <MfaSetup />}
      <a href="#" className="sub" onClick={async e => { e.preventDefault(); await api.post('/auth/logout').catch(() => {}); qc.clear(); location.href = '/login'; }}>{t('nav.logout')}</a>
    </div></div>
  );
}

// ---------- Bot check (Cloudflare Turnstile) ----------
declare global {
  interface Window { turnstile?: { render: (el: HTMLElement, o: Record<string, unknown>) => string; reset: (id?: string) => void; remove: (id: string) => void } }
}
let scriptLoad: Promise<void> | null = null;
function loadTurnstile() {
  scriptLoad ??= new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true; s.onload = () => res(); s.onerror = () => { scriptLoad = null; rej(new Error('turnstile')); };
    document.head.appendChild(s);
  });
  return scriptLoad;
}
/** Renders the widget when the server has a site key; calls onToken with the token ('' = expired). No key → renders nothing. */
export function BotCheck({ onToken }: { onToken: (token: string) => void }) {
  const { i18n } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const cfg = useQuery({ queryKey: ['public-config'], queryFn: () => api.get<{ turnstile_site_key: string | null }>('/public-config'), staleTime: Infinity });
  const key = cfg.data?.turnstile_site_key;
  useEffect(() => {
    if (!key || !ref.current) return;
    let id: string | undefined;
    let cancelled = false;
    void loadTurnstile().then(() => {
      if (cancelled || !ref.current || !window.turnstile) return;
      id = window.turnstile.render(ref.current, {
        sitekey: key, language: i18n.language, theme: 'auto',
        callback: (tkn: string) => onToken(tkn), 'expired-callback': () => onToken(''), 'error-callback': () => onToken('')
      });
    }).catch(() => {});
    return () => { cancelled = true; if (id && window.turnstile) window.turnstile.remove(id); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return key ? <div ref={ref} className="bot-check" /> : null;
}
/** Whether the bot check must be passed before submitting */
export function useBotCheckRequired() {
  const cfg = useQuery({ queryKey: ['public-config'], queryFn: () => api.get<{ turnstile_site_key: string | null }>('/public-config'), staleTime: Infinity });
  return !!cfg.data?.turnstile_site_key;
}
